import { useEffect, useRef, useState } from "react";
import { Download, Printer } from "lucide-react";
import { toast } from "sonner";

function wrapText(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

export default function GreetingCard({ greeting, sender }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const S = 1024;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const draw = (img) => {
      ctx.clearRect(0, 0, S, S);
      if (img) {
        const scale = Math.max(S / img.width, S / img.height);
        ctx.drawImage(img, (S - img.width * scale) / 2, (S - img.height * scale) / 2, img.width * scale, img.height * scale);
      } else {
        ctx.fillStyle = "#FDFBF7"; ctx.fillRect(0, 0, S, S);
        ctx.strokeStyle = "#F9A826"; ctx.lineWidth = 10; ctx.strokeRect(30, 30, S - 60, S - 60);
      }
      ctx.textAlign = "center";
      let y = 300;
      ctx.fillStyle = "#0A192F";
      ctx.font = "700 58px 'Playfair Display', Georgia, serif";
      wrapText(ctx, greeting.title, S - 260).forEach((l) => { ctx.fillText(l, S / 2, y); y += 70; });
      y += 20;
      ctx.font = "500 34px Manrope, sans-serif";
      ctx.fillStyle = "#334155";
      wrapText(ctx, greeting.message, S - 320).slice(0, 5).forEach((l) => { ctx.fillText(l, S / 2, y); y += 48; });
      if (greeting.message_hi) {
        y += 10;
        ctx.font = "500 30px Manrope, sans-serif";
        wrapText(ctx, greeting.message_hi, S - 320).slice(0, 4).forEach((l) => { ctx.fillText(l, S / 2, y); y += 44; });
      }
      if (greeting.quote) {
        y += 16;
        ctx.font = "italic 500 28px Georgia, serif";
        ctx.fillStyle = "#0A192F";
        wrapText(ctx, `“${greeting.quote}”`, S - 340).slice(0, 3).forEach((l) => { ctx.fillText(l, S / 2, y); y += 40; });
        if (greeting.quote_author) { ctx.font = "700 26px Manrope, sans-serif"; ctx.fillText(`— ${greeting.quote_author}`, S / 2, y); y += 40; }
      }
      const sName = sender?.organisation || sender?.name || "Sahaj Sarathi Foundation";
      ctx.font = "700 30px Manrope, sans-serif";
      ctx.fillStyle = "#0A192F";
      ctx.fillText(sName, S / 2, S - 110);
      ctx.font = "500 24px Manrope, sans-serif";
      ctx.fillStyle = "#64748B";
      const sub = [sender?.sender_name && sender.sender_name !== sName ? sender.sender_name : "", sender?.designation || "", sender?.website || "www.sahajsarathi.com"].filter(Boolean).join("  •  ");
      ctx.fillText(sub, S / 2, S - 70);
      setReady(true);
    };
    if (greeting.image) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => draw(img);
      img.onerror = () => draw(null);
      img.src = greeting.image;
    } else draw(null);
  }, [greeting, sender]);

  const download = () => {
    try {
      const a = document.createElement("a");
      a.download = `${(greeting.title || "greeting").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
      a.href = canvasRef.current.toDataURL("image/png");
      a.click();
      toast.success("High-quality image downloaded");
    } catch (e) {
      toast.error("Download blocked (image from external source). Upload the background to the site first.");
    }
  };

  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<img src="${canvasRef.current.toDataURL("image/png")}" style="width:100%" onload="window.print()"/>`);
  };

  return (
    <div data-testid="greeting-card-preview">
      <canvas ref={canvasRef} width={S} height={S} className="w-full rounded-xl border border-gray-200 shadow-lg" />
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" onClick={download} disabled={!ready} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="card-download"><Download size={15} /> Download PNG</button>
        <button type="button" onClick={print} disabled={!ready} className="btn-ghost !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="card-print"><Printer size={15} /> Print</button>
      </div>
    </div>
  );
}
